import { Link, useLocation } from 'react-router';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';

interface BreadcrumbItem {
  name: string;
  href: string;
}

// Route to breadcrumb name mapping
const routeNames: Record<string, string> = {
  '/': 'Dashboard',
  '/credentials': 'Credentials',
  '/credentials/new': 'New Credential',
  '/folders': 'Folders',
  '/share-links': 'Share Links',
  '/settings': 'Settings',
  '/profile': 'Profile',
  '/admin': 'Admin',
  '/admin/users': 'Users',
  '/admin/invites': 'Invites',
  '/admin/audit-logs': 'Audit Logs',
  '/archived': 'Archived',
};

export default function Breadcrumbs() {
  const location = useLocation();
  const pathParts = location.pathname.split('/').filter(Boolean);

  // Build breadcrumb items from path
  const breadcrumbs: BreadcrumbItem[] = [];
  let currentPath = '';

  for (const part of pathParts) {
    currentPath += `/${part}`;

    // Check if we have a name for this route
    let name = routeNames[currentPath];

    // If not found, try to infer from path
    if (!name) {
      // Check if this looks like a UUID (credential/folder detail page)
      if (part.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        name = 'Details';
      } else if (part === 'edit') {
        name = 'Edit';
      } else {
        // Capitalize and replace hyphens with spaces
        name = part
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
    }

    breadcrumbs.push({
      name,
      href: currentPath,
    });
  }

  // Don't show breadcrumbs on dashboard
  if (location.pathname === '/') {
    return null;
  }

  return (
    <nav className="flex items-center space-x-2 text-sm mb-6" aria-label="Breadcrumb">
      <Link
        to="/"
        className="text-gray-400 hover:text-gray-600 transition-colors"
      >
        <HomeIcon className="h-4 w-4" />
        <span className="sr-only">Home</span>
      </Link>

      {breadcrumbs.map((item, index) => (
        <div key={item.href} className="flex items-center space-x-2">
          <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
          {index === breadcrumbs.length - 1 ? (
            <span className="text-gray-900 font-medium">{item.name}</span>
          ) : (
            <Link
              to={item.href}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              {item.name}
            </Link>
          )}
        </div>
      ))}
    </nav>
  );
}
